import {
  Button,
  Dialog,
  DialogFooter,
  Flag,
  Form,
  FormButton,
  FormControl,
  FormField,
  FormSubmitHandler,
  Separator,
} from 'platform/components';
import {Box, Hide, HStack, Stack, Text, useDevice, VStack} from 'platform/foundation';
import type {z} from 'zod';

import {useFormState, useWatch} from 'react-hook-form';

import {TestIdProps, suffixTestId} from 'shared/web';

import {useCatalogue} from '../../../../../hooks/useCatalogue';
import i18n from '../../../../../i18n/i18n';
import {RelativeFilterConfigBody, RelativeFilterResponseBody} from '../../../../../types/api/api';
import {getPresetTags} from '../utils/getPresetTags';
import {
  PresetEditorFormValues,
  buildPresetEditorDefaultValues,
  getRelativeFilterConfigFromPresetEditorForm,
  presetEditorFormSchema,
} from '../utils/presetEditorFormSchema';
import {BoundariesSection} from './BoundariesSection';
import {PropertiesSection} from './PropertiesSection';

interface PresetEditorDialogProps extends TestIdProps {
  isOpen: boolean;
  /** null means create mode */
  initialPreset: RelativeFilterResponseBody | null;
  onSave: (name: string, config: RelativeFilterConfigBody, isDefault: boolean) => void;
  onDelete?: () => void;
  onClose: () => void;
}

type PresetEditorDialogFormBodyProps = PresetEditorDialogProps & {
  control: FormControl<PresetEditorFormValues>;
  isConfigReadOnly: boolean;
  isSystemPreset: boolean;
};

const PRESET_SUMMARY_CONFIG_WATCH_NAMES = [
  'year',
  'mileage',
  'mileagePct',
  'power',
  'powerPct',
  'price',
  'pricePct',
  'featureScore',
  'trimLevel',
  'transmission',
  'fuel',
  'body',
  'drive',
  'vatDeductible',
  'sellerType',
  'seats',
  'features',
  'radius',
] as const;

function PresetEditorDialogFormBody(props: PresetEditorDialogFormBodyProps) {
  const {control, isConfigReadOnly, isSystemPreset} = props;

  const [
    wYear,
    wMileage,
    wMileagePct,
    wPower,
    wPowerPct,
    wPrice,
    wPricePct,
    wFeatureScore,
    wTrimLevel,
    wTransmission,
    wFuel,
    wBody,
    wDrive,
    wVatDeductible,
    wSellerType,
    wSeats,
    wFeatures,
    wRadius,
  ] = useWatch({
    control,
    name: [...PRESET_SUMMARY_CONFIG_WATCH_NAMES],
  });

  const summaryConfig: RelativeFilterConfigBody = {
    year: wYear ?? null,
    mileage: wMileage ?? null,
    mileagePct: wMileagePct ?? null,
    power: wPower ?? null,
    powerPct: wPowerPct ?? null,
    price: wPrice ?? null,
    pricePct: wPricePct ?? null,
    featureScore: wFeatureScore ?? null,
    trimLevel: wTrimLevel ?? null,
    transmission: wTransmission ?? null,
    fuel: wFuel ?? null,
    body: wBody ?? null,
    drive: wDrive ?? null,
    vatDeductible: wVatDeductible ?? null,
    sellerType: wSellerType ?? null,
    seats: wSeats ?? null,
    features: wFeatures ?? null,
    radius: wRadius ?? null,
  };

  const {getFeatureLabel, hasFeatureTranslation, isLoading: isCatalogueLoading} = useCatalogue();
  const {isValid} = useFormState<PresetEditorFormValues>();
  const summaryTags = getPresetTags(summaryConfig, {
    getFeatureLabel,
    isKnownFeature: hasFeatureTranslation,
  });

  return (
    <>
      <VStack spacing={4}>
        <Stack direction={['column', 'row']} align="flex-start" justify="space-between" spacing={4}>
          <Box width={68} flexShrink={0}>
            <FormField
              control={control}
              name="name"
              type="text"
              label={i18n.t('entity.filterForm.savedFilters.dialog.presetName')}
              isRequired
              isDisabled={isConfigReadOnly}
              data-testid={suffixTestId('nameInput', props)}
            />
          </Box>
          {summaryTags.length > 0 ? (
            <HStack spacing={1} justify={[undefined, 'flex-end']} wrap>
              {summaryTags.map((label, index) => (
                <Flag
                  // We do not have unique tags, so we use the index as the key
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${index}-${label}`}
                  label={label}
                  size="small"
                  colorScheme="blue"
                  isSubtle
                />
              ))}
            </HStack>
          ) : (
            <Text size="xSmall" color="secondary">
              {i18n.t('entity.filterForm.savedFilters.summary.noConstraints')}
            </Text>
          )}
        </Stack>

        <Separator spacing={0} />

        <Stack direction={['column', 'row']} spacing={[4, 0]}>
          <Box flex={1} minWidth={0}>
            <BoundariesSection isReadOnly={isConfigReadOnly} data-testid={props['data-testid']} />
          </Box>
          <Separator orientation="vertical" spacing={4} />
          <Box flex={1} minWidth={0}>
            <PropertiesSection isReadOnly={isConfigReadOnly} data-testid={props['data-testid']} />
          </Box>
        </Stack>
      </VStack>

      <DialogFooter data-testid={suffixTestId('footer', props)}>
        <HStack justify="space-between" align="center">
          <HStack spacing={3} align="center">
            <Hide when={isSystemPreset || !props.onDelete}>
              <Button
                title={i18n.t('entity.filterForm.savedFilters.dialog.delete')}
                leftIcon="action/delete_outline"
                variant="dangerOutlined"
                type="button"
                onClick={props.onDelete}
                data-testid={suffixTestId('deleteButton', props)}
              />
            </Hide>
            <FormField
              control={control}
              name="isDefault"
              type="checkbox"
              label={i18n.t('entity.filterForm.savedFilters.dialog.setAsDefault')}
              data-testid={suffixTestId('setAsDefaultCheckbox', props)}
            />
          </HStack>
          <HStack spacing={2} justify="flex-end">
            <Button
              title={i18n.t('entity.filterForm.savedFilters.dialog.cancel')}
              variant="secondary"
              type="button"
              onClick={props.onClose}
              data-testid={suffixTestId('cancelButton', props)}
            />
            <FormButton
              control={control}
              type="submit"
              title={i18n.t('entity.filterForm.savedFilters.dialog.save')}
              isDisabled={!isValid || isCatalogueLoading}
              data-testid={suffixTestId('saveButton', props)}
            />
          </HStack>
        </HStack>
      </DialogFooter>
    </>
  );
}

export function PresetEditorDialog(props: PresetEditorDialogProps) {
  const isSystemPreset = props.initialPreset?.system ?? false;
  const isConfigReadOnly = isSystemPreset;
  const {hasFeatureTranslation, isLoading: isCatalogueLoading} = useCatalogue();
  const device = useDevice();
  const isSmallScreen = device === 'mobile' || device === 'tablet';
  const modalSize = isSmallScreen ? 'xLarge' : undefined;

  const defaultValues = buildPresetEditorDefaultValues(props.initialPreset);

  const onSubmit: FormSubmitHandler<PresetEditorFormValues> = (values) => {
    if (isCatalogueLoading) {
      return Promise.resolve();
    }

    props.onSave(
      values.name.trim(),
      getRelativeFilterConfigFromPresetEditorForm(values, hasFeatureTranslation),
      values.isDefault
    );
    return Promise.resolve();
  };

  return (
    <Dialog
      title={i18n.t('entity.filterForm.savedFilters.dialog.title')}
      isOpen={props.isOpen}
      onClose={props.onClose}
      size={modalSize}
      withAdditionalFooter
      scrollBehavior="inside"
      data-testid={suffixTestId('dialog', props)}
    >
      <Form<PresetEditorFormValues>
        experimentalZodSchema={presetEditorFormSchema as z.ZodSchema<PresetEditorFormValues>}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        mode="onChange"
      >
        {(control) => (
          <PresetEditorDialogFormBody
            control={control}
            isConfigReadOnly={isConfigReadOnly}
            isSystemPreset={isSystemPreset}
            {...props}
          />
        )}
      </Form>
    </Dialog>
  );
}
